import React, { useState, useCallback, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { FaFilePdf, FaSpinner } from 'react-icons/fa'
import { COMPANY, resolveArchOfficeInfo, getOfficeState } from '../../lib/estimateData'
import {
    MARKET_OPTIONS, DIFFICULTY_LEVELS, ARCH_PROJECT_TYPES,
    DISCIPLINE_SCOPE_OPTIONS, DISCIPLINE_SCOPE_MULTIPLIERS,
    ADD_ALT_PROJECT_TYPES, getProjectTypeComResPrefix,
    normalizeDiscipline,
} from '../../lib/archConfig'
import {
    applyAddonGatedFilter,
    isAddonItem,
    getAddonLabel,
    evaluateAddonRule,
} from '../../lib/archAddons'
import { cleanTermText } from '../../lib/archTerms'
import jsPDF, { AcroFormTextField } from 'jspdf'

const fmt = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const r2  = (n) => Math.round((Number(n) || 0) * 100) / 100

function formatTimeFrame(value, unit, t) {
  const v = Number(value) || 0
  const bd = t('admin.arch.time_frame_business_days')
  if (unit === 'days') return `${v} ${bd}`
  if (unit === 'weeks') return `${v} ${t('admin.arch.time_frame_unit_weeks')} (${v * 5} ${bd})`
  if (unit === 'months') return `${v} ${t('admin.arch.time_frame_unit_months')} (${v * 20} ${bd})`
  return `${v} ${t('admin.arch.time_frame_unit_' + (unit || 'weeks'))}`
}

// Derive company name + address from payment method (primary) or ARCH_OFFICE_INFO (fallback)
function getFooterOfficeInfo(estimate, paymentMethods) {
  const officeState = getOfficeState(estimate)
  const activeMethods = (paymentMethods || [])
    .filter(m => m.is_active !== false)
    .filter(m => m.office_key === officeState || m.office_key === 'both')
    .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
  const primary = activeMethods[0] || null
  const fallback = resolveArchOfficeInfo(estimate)
  return {
    companyName: primary?.account_name || fallback.companyName,
    address:     primary?.bank_address || fallback.address,
    phone:       fallback.phone,
  }
}

// ── Brand
const BRAND     = '#D9A405'
const BRAND_BG  = '#fffbea'
const BRAND_BD  = '#fde68a'

// ── Date helpers
function fmtDate(dateStr, locale = 'en-US') {
    if (!dateStr) return '—'
    const d = new Date(dateStr + 'T12:00:00')
    return d.toLocaleDateString(locale, { year: 'numeric', month: 'long', day: 'numeric' })
}
function validUntil(dateStr, locale = 'en-US') {
    if (!dateStr) return '—'
    const d = new Date(dateStr + 'T12:00:00')
    d.setDate(d.getDate() + 30)
    return d.toLocaleDateString(locale, { year: 'numeric', month: 'long', day: 'numeric' })
}

function getActiveTerms(estimate) {
    const sorted = (estimate?.terms || [])
        .filter(t => t.included && !t.is_section_title && t.source_file !== 'what_you-should_know')
        .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
    const seen = new Set()
    return sorted.filter(t => {
        const key = (t.text || '').trim().toLowerCase()
        if (seen.has(key)) return false
        seen.add(key)
        return true
    })
}

function getWhatYouShouldKnowTerm(estimate) {
    return (estimate?.terms || []).find(t =>
        t.source_file === 'what_you-should_know' && t.included === true
    ) || null
}

function getWhatYouShouldKnowText(estimate) {
    const term = getWhatYouShouldKnowTerm(estimate)
    if (term && (term.text || '').trim()) {
        return cleanTermText(term.text)
    }
    return ''
}

function getTimeGreeting(t) {
    const hour = new Date().getHours()
    if (hour < 12) return t('admin.arch.greeting_morning')
    if (hour < 18) return t('admin.arch.greeting_afternoon')
    return t('admin.arch.greeting_evening')
}

function getActiveTermsGrouped(estimate) {
    const active = getActiveTerms(estimate)
    const groups = []
    let current = null
    for (const term of active) {
        const secTitle = term.section_title || ''
        if (!current || current.title !== secTitle) {
            current = { title: secTitle, items: [] }
            groups.push(current)
        }
        current.items.push(term)
    }
    return groups
}

// ── Discipline → pct key
const DISC_ALIAS = {
    arch: 'archPct', architecture: 'archPct', architectural: 'archPct',
    structural: 'structPct', struct: 'structPct', structure: 'structPct', estructural: 'structPct',
    mep: 'mepPct', mechanical: 'mepPct', electrical: 'mepPct', plumbing: 'mepPct',
    civil: 'civilPct', site: 'civilPct', civil_dev: 'civilPct',
    ecc: 'adminPct', energy: 'adminPct',
    admin: 'adminPct', pm: 'adminPct', 'project management': 'adminPct', management: 'adminPct', support: 'adminPct',
}
const DISC_ORDER = ['ARCH', 'ARCHITECTURE', 'STRUCT', 'STRUCTURAL', 'MEP', '3D', 'CIVIL', 'ECC', 'ADMIN', 'PM']


// ── Hex → RGB for jsPDF
function hex(c) {
    const v = parseInt(c.replace('#', ''), 16)
    return { r: (v >> 16) & 255, g: (v >> 8) & 255, b: v & 255 }
}
function matchDisciplinePct(d) {
    if (!d) return null
    const lower = d.toLowerCase().trim()
    if (DISC_ALIAS[lower]) return DISC_ALIAS[lower]
    for (const [key, val] of Object.entries(DISC_ALIAS)) {
        if (lower.includes(key)) return val
    }
    return null
}

// ── Token-overlap suppression
const STOP_WORDS = new Set([
    'city','from','with','required','upon','included','including','and','the','for','not',
    'that','this','are','will','some','their','can','quoted','separately','needed','approval',
    'request','client','responsibility','installed','code','depth','fees','plan','plans',
    'if','by','or','to','of','in','is','it','as','an','be','at','on','any','per','min',
])
const tokenize = (s) => s.toLowerCase().split(/[\s,;:()\-/]+/).filter(w => w.length >= 4 && !STOP_WORDS.has(w))
const hasTokenOverlap = (excB, incB, thr = 2) => {
    const et = new Set(tokenize(excB)); let n = 0
    for (const t of tokenize(incB)) { if (et.has(t) && ++n >= thr) return true }
    return false
}

// ── Discipline styles (preview / PDF — uniform yellow)
const DISC_STYLE = {
    DEFAULT: { bg: '#f3f4f6', text: '#6b7280', border: '#d1d5db' },
}
function ds() {
    return DISC_STYLE.DEFAULT
}

// ── Phase pill styles + helpers
const PHASE_PILL_STYLE = {
    cds:    { bg: '#dbeafe', text: '#1e40af', border: '#93c5fd' },
    design: { bg: '#ede9fe', text: '#5b21b6', border: '#c4b5fd' },
}
function getDiscPhases(items) {
    const phases = new Set()
    for (const it of items) {
        if (it.phase) phases.add(it.phase)
    }
    return [...phases]
}
function getGroupPhaseMode(items) {
    const phases = getDiscPhases(items)
    if (phases.length === 1) return 'single'
    if (phases.length > 1) return 'mixed'
    return 'none'
}
function PhasePill({ phase, t }) {
    const s = PHASE_PILL_STYLE[phase] || { bg: '#f3f4f6', text: '#6b7280', border: '#d1d5db' }
    const label = phase === 'cds' ? t('admin.arch.phase_cds_short') : phase === 'design' ? t('admin.arch.phase_design_short') : phase
    return (
        <span style={{
            display: 'inline-block', background: s.bg, color: s.text,
            border: `1px solid ${s.border}`, borderRadius: 4,
            fontSize: 8, fontWeight: 800, padding: '1px 5px',
            textTransform: 'uppercase', letterSpacing: '0.5px',
            whiteSpace: 'nowrap', marginLeft: 6, verticalAlign: 'middle',
        }}>
            {label}
        </span>
    )
}

function drawPhasePillPDF(pdf, phase, x, y, t) {
    const s = PHASE_PILL_STYLE[phase]
    if (!s) return x
    const label = phase === 'cds' ? t('admin.arch.phase_cds_short') : phase === 'design' ? t('admin.arch.phase_design_short') : phase
    const padX = 4, padY = 2
    pdf.setFontSize(7)
    pdf.setFont('helvetica', 'bold')
    pdf.setTextColor(26, 26, 26)
    const tw = pdf.getTextWidth(label)
    const pillW = tw + padX * 2
    const pillH = 10
    const rgb = hex(s.bg), brgb = hex(s.border), trgb = hex(s.text)
    pdf.setFillColor(rgb.r, rgb.g, rgb.b)
    pdf.setDrawColor(brgb.r, brgb.g, brgb.b)
    pdf.setLineWidth(0.5)
    pdf.roundedRect(x, y - pillH + 3, pillW, pillH, 2, 2, 'FD')
    pdf.setTextColor(trgb.r, trgb.g, trgb.b)
    pdf.text(label, x + padX, y - 1)
    pdf.setTextColor(0, 0, 0)
    return x + pillW + 3
}

// ── Translation helpers (replaced static lookups with t() calls inline)

// Strict PDF discipline filtering — only primary disciplines per scope
const PDF_DISCIPLINE_SCOPE_ALLOWED = {
    full:        ['ARCH', 'STRUCT', 'MEP', '3D', 'CIVIL', 'ECC', 'ADMIN'],
    arch_struct:  ['ARCH', 'STRUCT', '3D'],
    arch_mep:     ['ARCH', 'MEP', '3D'],
    arch_civil:   ['ARCH', 'CIVIL', '3D'],
    struct_civil: ['STRUCT', 'CIVIL', '3D'],
    mep_civil:    ['MEP', 'CIVIL', '3D'],
    solo_arch:    ['ARCH', '3D'],
    solo_struct:  ['STRUCT', '3D'],
    solo_mep:     ['MEP', '3D'],
    solo_civil:   ['CIVIL', '3D'],
}

// ── Canonical discipline breakdown rows
// For Add/Alt/Rem projects civil engineering is not part of the discipline split,
// so civil rows are omitted from the proposal/PDF breakdown regardless of state.
const DISC_BREAKDOWN = (estimate, t) => {
    const isAddAlt = ADD_ALT_PROJECT_TYPES.has(estimate?.projectType)
    const rows = [
        { key: 'archPct',       name: ((estimate?.structPct || 0) + (estimate?.mepPct || 0) + (isAddAlt ? 0 : (estimate?.civilPct || 0)) === 0) ? t('admin.arch.disc_arch') : t('admin.arch.disc_arch_eng'), disc: 'ARCH'       },
        { key: 'structPct',     name: t('admin.arch.disc_struct'),                             disc: 'STRUCT'     },
        { key: 'mepPct',        name: t('admin.arch.disc_mep'),                                disc: 'MEP'        },
        { key: 'adminPct',      name: t('admin.arch.disc_admin'),                              disc: 'ADMIN'      },
    ]
    if (!isAddAlt) {
        rows.push(
            { key: 'civilPct',      name: t('admin.arch.disc_civil'),                              disc: 'CIVIL'      },
            { key: 'civilAdminPct', name: t('admin.arch.disc_civil_admin') || 'C/MP/QA&QC (civil)', disc: 'CIVIL_ADMIN' }
        )
    }
    return rows
}

// ── Shared table cell styles
const TH = {
    fontSize: 9, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '1px',
    color: '#888', textAlign: 'left', padding: '6px 10px',
    background: '#f9fafb', borderBottom: '1px solid #e5e5e5',
}
const TD = { fontSize: 11, padding: '4px 10px', borderBottom: '1px solid #f0f0f0', verticalAlign: 'top' }

// ── Section heading
function SectionHead({ title, subtitle }) {
    return (
        <div style={{ marginBottom: 10, paddingBottom: 5, borderBottom: '2px solid #1a1a1a', pageBreakAfter: 'avoid', display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <span style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '1.5px', color: '#1a1a1a' }}>{title}</span>
            {subtitle && <span style={{ fontSize: 9, color: '#888', fontWeight: 500 }}>{subtitle}</span>}
        </div>
    )
}

// ── Chip
function Chip({ disc }) {
    const s = ds(disc)
    return (
        <span style={{ background: s.bg, color: s.text, border: `1px solid ${s.border}`, borderRadius: 4, fontSize: 9, fontWeight: 700, padding: '2px 6px', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
            {disc}
        </span>
    )
}

// ─────────────────────────────────────────────────────────────────────────────
function ArchProposalDocument({ estimate, mode = 'formal', difficultyLevels, paymentInfo, t: tProp, locale = 'en-US', session, addonDefs = [] }) {
    const { t: tApp } = useTranslation()
    const t = tProp || tApp
    const contentRef = useRef(null)
    const [pageCount, setPageCount] = useState(1)
    const PAGE_HEIGHT = 900 // approximate content height per Letter page

    useEffect(() => {
        const el = contentRef.current
        if (!el) return
        const raf = requestAnimationFrame(() => {
            const h = el.scrollHeight
            const pages = Math.max(1, Math.ceil(h / PAGE_HEIGHT))
            setPageCount(pages)
        })
        return () => cancelAnimationFrame(raf)
    }, [estimate, mode])

    const isPrelim      = mode === 'preliminary'
    const isAddAlt      = ADD_ALT_PROJECT_TYPES.has(estimate.projectType)
    const allScopeItems = estimate.scopeItems  || []
    const totalContract = Number(estimate.totalContract) || 0
    const salePrice     = Number(estimate.salePrice) || 0
    const paySchedule   = estimate.cuotas || []
    const schedTotal    = paySchedule.reduce((s, c) => s + (Number(c.pct) || 0), 0)
    const schedValid    = Math.abs(schedTotal - 100) < 0.01

    // Backward-compat: paymentInfo may be a single object or an array
    const paymentInfos = Array.isArray(paymentInfo) ? paymentInfo : paymentInfo ? [paymentInfo] : []
    const estimateState = getOfficeState(estimate)
    const activeMethods = paymentInfos
        .filter(m => m.is_active !== false)
        .filter(m => m.office_key === estimateState || m.office_key === 'both')
        .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))

    const diffLabelMap = difficultyLevels
        ? Object.fromEntries(difficultyLevels.map(d => [String(d.value), `${t(d.labelKey) || d.label || ''} — ${t(d.descKey) || d.desc || ''}`]))
        : Object.fromEntries(DIFFICULTY_LEVELS.map(d => [String(d.value), `${t(d.labelKey) || d.label || ''} — ${t(d.descKey) || d.desc || ''}`]))

    // Filter scope items by discipline scope (strict — only primary disciplines).
    // For Add/Alt/Rem projects civil engineering is never part of the deliverable set.
    const allowedDiscs = (ADD_ALT_PROJECT_TYPES.has(estimate.projectType)
        ? (PDF_DISCIPLINE_SCOPE_ALLOWED[estimate.disciplineScope] || PDF_DISCIPLINE_SCOPE_ALLOWED.full).filter(d => d !== 'CIVIL')
        : (PDF_DISCIPLINE_SCOPE_ALLOWED[estimate.disciplineScope] || PDF_DISCIPLINE_SCOPE_ALLOWED.full))
    const gatedItemsRaw = applyAddonGatedFilter(
        allScopeItems.filter(i => allowedDiscs.includes(normalizeDiscipline(i.discipline))),
        estimate.addons,
        addonDefs
    )
    // Filter out certification items whose COM/RES suffix doesn't match the project type
    const ptPrefix = getProjectTypeComResPrefix(estimate.projectType)
    const gatedItems = gatedItemsRaw.filter(item => {
        const name = (item.name || '').trim()
        const comMatch = /[\s–—-]+COM\s*$/i.test(name)
        const resMatch = /[\s–—-]+RES\s*$/i.test(name)
        if (!comMatch && !resMatch) return true
        if (ptPrefix === 'com') return comMatch
        if (ptPrefix === 'res') return resMatch
        return false
    })
    // Separate add-on items from discipline deliverables — add-on items appear only in ADD-ONS section
    const scopeItems = gatedItems.filter(i => !isAddonItem(i, addonDefs))
    const addonItems = gatedItems.filter(i => isAddonItem(i, addonDefs))

    // Group scope items by discipline in canonical order
    const discMap = {}
    for (const item of scopeItems) {
        const d = normalizeDiscipline(item.discipline)
        if (!discMap[d]) discMap[d] = []
        discMap[d].push(item)
    }
    const seenDiscs = Object.keys(discMap)
    const discOrder = [
        ...DISC_ORDER.filter(d => seenDiscs.includes(d)),
        ...seenDiscs.filter(d => !DISC_ORDER.includes(d)).sort(),
    ]

    // Inc / Exc bullets from VISIBLE (non-addon) items only, with dedup + overlap suppression
    const allInc = [], allExc = []
    const seenInc = new Set(), seenExc = new Set()
    for (const item of scopeItems) {
        for (const b of (item.inc || [])) { if (!seenInc.has(b)) { seenInc.add(b); allInc.push(b) } }
        for (const b of (item.exc || [])) { if (!seenExc.has(b)) { seenExc.add(b); allExc.push(b) } }
    }
    const filteredExc = allExc.filter(e => !allInc.some(i => hasTokenOverlap(e, i)))

    // Active discipline breakdown rows (only disciplines with pct > 0 and sale price > 0)
    const activeDiscs = DISC_BREAKDOWN(estimate, t).filter(r => Number(estimate[r.key] || 0) > 0 && salePrice > 0)
    const activeAddons = (estimate.addons || [])
        .filter(a => a.enabled === true)
        .sort((a, b) => {
            const defA = addonDefs.find(d => d.key === a.key)
            const defB = addonDefs.find(d => d.key === b.key)
            return (defA?.sort_order || 0) - (defB?.sort_order || 0)
        })

    // Map each enabled add-on to its related deliverable names for sub-list rendering
    const addonItemMap = {}
    for (const addon of activeAddons) {
        const def = addonDefs.find(d => d.key === addon.key)
        if (def) {
            addonItemMap[addon.key] = addonItems
                .filter(i => evaluateAddonRule(i, def))
                .map(i => i.name)
        }
    }
    // Non-percentage disciplines that have scope items (e.g. 3D Services)
    const activeDiscSet = new Set(activeDiscs.map(r => r.disc))
    const extraDiscs = discOrder.filter(d => !activeDiscSet.has(d) && (discMap[d] || []).length > 0)

    let rowIdx = 0

    return (
        <div
            ref={contentRef}
            id={isPrelim ? 'arch-estimate-preliminary' : 'arch-estimate-content'}
            style={{ fontFamily: 'Arial, Helvetica, sans-serif', color: '#1a1a1a', background: '#fff', position: 'relative', ...(isPrelim ? { textAlign: 'justify' } : {}) }}
        >
            {/* ── Header image */}
            <img src="/header.png" alt="" style={{ width: '100%', display: 'block' }} />

            <div style={{ padding: '20px 36px 28px' }}>

                {/* ── Preliminary banner */}
                {isPrelim && (
                    <div style={{ background: BRAND_BG, border: `1px solid ${BRAND_BD}`, borderRadius: 6, padding: '6px 14px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{ fontSize: 9, fontWeight: 900, textTransform: 'uppercase', letterSpacing: '2px', color: '#92400e' }}>
                            ⚡ {t('admin.arch.preliminary_banner')}
                        </span>
                    </div>
                )}

                {/* ── Document title bar */}
                <div style={{ borderLeft: `4px solid ${BRAND}`, paddingLeft: 14, marginBottom: 22, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                        <div style={{ fontSize: 14, fontWeight: 900, textTransform: 'uppercase', letterSpacing: '2px', color: '#1a1a1a', lineHeight: 1.2 }}>
                            {isPrelim ? t('admin.arch.preliminary_title') : t('admin.arch.proposal_title_design_services')}
                        </div>
                        <div style={{ fontSize: 10, color: '#888', marginTop: 4 }}>
                            {isPrelim ? t('admin.arch.meta_estimate') : t('admin.arch.meta_proposal')} <strong style={{ color: '#555' }}>#{estimate.number || '—'}</strong>
                            &nbsp;·&nbsp; {t('admin.arch.meta_issued')} {fmtDate(estimate.date, locale)}
                            {!isPrelim && <>&nbsp;·&nbsp; {t('admin.arch.meta_valid_through')} <strong style={{ color: '#555' }}>{validUntil(estimate.date, locale)}</strong></>}
                        </div>
                        {estimate.projectDescription && (
                            <div style={{ fontSize: 10, color: '#555', marginTop: 4, fontStyle: 'italic' }}>
                                {estimate.projectDescription}
                            </div>
                        )}
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0, marginLeft: 20 }}>
                        <div style={{ fontSize: 9, color: '#aaa', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 2 }}>{t('admin.arch.meta_status')}</div>
                        <div style={{ fontSize: 11, fontWeight: 800, textTransform: 'capitalize', color: '#1a1a1a' }}>{t('admin.arch.status_' + (estimate.status || 'draft'))}</div>
                    </div>
                </div>

                {/* ── Client & Project info grid */}
                <div style={{ marginBottom: 20 }}>
                    <SectionHead title={t('admin.arch.client_project_info')} />
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', border: '1px solid #e5e5e5', borderRadius: 6, overflow: 'hidden' }}>
                        {[
                            { label: t('admin.arch.preview_client_name'),      value: estimate.client  || '—' },
                            { label: t('admin.arch.preview_project_address'),  value: estimate.address || '—' },
                            { label: t('admin.arch.preview_phone'),            value: estimate.phone   || '—' },
                            { label: t('admin.arch.preview_email'),            value: estimate.email   || '—' },
                            { label: t('admin.arch.preview_project_type'),     value: estimate.projectType ? (t('admin.arch.ptype_' + estimate.projectType) || estimate.projectType) : '—' },
                            { label: t('admin.arch.preview_area'),             value: isAddAlt
                                ? (
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))', gap: 8 }}>
                                        {[
                                            { label: t('admin.arch.add_alt_existente'), sqft: estimate.existSqft, diff: estimate.existDiff },
                                            { label: t('admin.arch.add_alt_remodel'),   sqft: estimate.remSqft,   diff: estimate.remDiff },
                                            { label: t('admin.arch.add_alt_adicion'),    sqft: estimate.addSqft,   diff: estimate.addDiff },
                                        ].filter(col => Number(col.sqft || 0) > 0).map((col, i) => (
                                            <div key={i}>
                                                <div style={{ fontSize: 8, fontWeight: 700, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 1 }}>{col.label}</div>
                                                <div style={{ fontSize: 10, color: '#1a1a1a' }}>
                                                    {Number(col.sqft).toLocaleString()} {t('admin.arch.area_sqft')}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )
                                : `${Number(estimate.sqft || 0).toLocaleString()} ${t('admin.arch.area_sqft')}`
                            },
                        ].map((cell, i) => (
                            <div key={i} style={{ padding: '5px 10px', background: Math.floor(i / 2) % 2 === 0 ? '#f9fafb' : '#fff', borderBottom: '1px solid #f0f0f0' }}>
                                {cell.label && <div style={{ fontSize: 9, fontWeight: 700, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 1 }}>{cell.label}</div>}
                                <div style={{ fontSize: 11, color: '#1a1a1a' }}>{cell.value}</div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* ── Intro paragraph */}
                {!isPrelim && (
                    <div style={{ marginBottom: 20 }}>
                        <p style={{ fontSize: 11, color: '#333', lineHeight: 1.6, margin: 0, fontStyle: 'italic', wordBreak: 'break-word', textAlign: 'justify' }}>
                            {t('admin.arch.intro_text', { address: estimate.address || '—' })}
                        </p>
                    </div>
                )}

                {/* ── Preliminary Intro */}
                {isPrelim && (
                    <div style={{ marginBottom: 20, pageBreakInside: 'avoid' }}>
                        <p style={{ fontSize: 11, color: '#333', lineHeight: 1.6, margin: '0 0 8px', textAlign: 'left' }}>
                            {getTimeGreeting(t)}
                        </p>
                        <p style={{ fontSize: 11, color: '#333', lineHeight: 1.6, margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', textAlign: 'justify' }}>
                            {t('admin.arch.preliminary_intro_greeting', { address: estimate.address || '—' })}
                        </p>
                    </div>
                )}

                {/* ── Scope of Work */}
                {estimate.notes && estimate.notes.trim() && (
                    <div style={{ marginBottom: 20 }}>
                        <SectionHead
                            title={t('admin.arch.preview_scope_of_work')}
                            subtitle={estimate.timeFrameValue && estimate.timeFrameUnit ? <span style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '1.5px', color: '#1a1a1a' }}>{t('admin.arch.estimated_completion')}: {formatTimeFrame(estimate.timeFrameValue, estimate.timeFrameUnit, t)}</span> : undefined}
                        />
                        <div style={{ padding: '12px 14px', background: '#fafafa', border: '1px solid #e5e5e5', borderRadius: 6 }}>
                            <p style={{ fontSize: 11, color: '#333', lineHeight: 1.6, margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', textAlign: 'justify' }}>{estimate.notes}</p>
                        </div>
                    </div>
                )}

                {/* ── Deliverables & Fee Allocation (unified) */}
                {(activeDiscs.length > 0 || activeAddons.length > 0) && (
                    <div style={{ marginBottom: 20 }}>
                        <SectionHead title={t('admin.arch.deliverables_fee')} />

                        {/* Discipline sections */}
                        {isPrelim ? (
                            <div style={{ marginBottom: 14 }}>
                                {activeDiscs.map(row => {
                                    const items = discMap[row.disc] || []
                                    const pct = Number(estimate[row.key] || 0)
                                    const base = estimate.useCivilSplit && (row.disc === 'CIVIL' || row.disc === 'CIVIL_ADMIN')
                                        ? (estimate.civilPrice || 0)
                                        : estimate.useCivilSplit
                                            ? (estimate.mainPrice || 0)
                                            : salePrice
                                    const amt = (base * pct) / 100
                                    return (
                                        <p key={row.key} style={{ fontSize: 11, color: '#1a1a1a', lineHeight: 1.6, margin: '0 0 6px', textAlign: 'justify' }}>
                                            <strong>{row.disc}: ${fmt(amt)}</strong>
                                            {getGroupPhaseMode(items) === 'single' && <PhasePill phase={getDiscPhases(items)[0]} t={t} />}
                                            {items.length > 0 ? ' — ' : ''}
                                            {items.map((i, idx) => (
                                                <span key={i.id || idx}>
                                                    {i.name}
                                                    {getGroupPhaseMode(items) === 'mixed' && i.phase && <PhasePill phase={i.phase} t={t} />}
                                                    {idx < items.length - 1 ? ', ' : ''}
                                                </span>
                                            ))}
                                        </p>
                                    )
                                })}
                                {extraDiscs.map(d => {
                                    const items = discMap[d] || []
                                    if (items.length === 0) return null
                                    return (
                                        <p key={d} style={{ fontSize: 11, color: '#1a1a1a', lineHeight: 1.6, margin: '0 0 6px', textAlign: 'justify' }}>
                                            <strong>{d}:</strong>
                                            {getGroupPhaseMode(items) === 'single' && <PhasePill phase={getDiscPhases(items)[0]} t={t} />}
                                            {' '}
                                            {items.map((i, idx) => (
                                                <span key={i.id || idx}>
                                                    {i.name}
                                                    {getGroupPhaseMode(items) === 'mixed' && i.phase && <PhasePill phase={i.phase} t={t} />}
                                                    {idx < items.length - 1 ? ', ' : ''}
                                                </span>
                                            ))}
                                        </p>
                                    )
                                })}
                            </div>
                        ) : (
                            <>
                                {activeDiscs.map(row => {
                                    const pct = Number(estimate[row.key] || 0)
                                    const base = estimate.useCivilSplit && (row.disc === 'CIVIL' || row.disc === 'CIVIL_ADMIN')
                                        ? (estimate.civilPrice || 0)
                                        : estimate.useCivilSplit
                                            ? (estimate.mainPrice || 0)
                                            : salePrice
                                    const amt = (base * pct) / 100
                                    const items = discMap[row.disc] || []
                                    return (
                                        <div key={row.key} style={{ marginBottom: 14, pageBreakInside: 'avoid' }}>
                                            {/* Discipline header bar */}
                                            <div style={{
                                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                                background: ds(row.disc).bg, border: `1px solid ${ds(row.disc).border}`,
                                                borderRadius: 6, padding: '6px 10px', marginBottom: 6,
                                            }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                    <Chip disc={row.disc} />
                                                    <span style={{ fontSize: 11, fontWeight: 700, color: ds(row.disc).text }}>{row.name}</span>
                                                    {getGroupPhaseMode(items) === 'single' && <PhasePill phase={getDiscPhases(items)[0]} t={t} />}
                                                </div>
                                                <span style={{ fontSize: 12, fontWeight: 800, color: '#1a1a1a' }}>${fmt(amt)}</span>
                                            </div>

                                            {/* Service items for this discipline */}
                                            {items.length > 0 && row.disc !== 'ADMIN' ? (
                                                <div style={{ paddingLeft: 8 }}>
                                                    {items.map((item, idx) => {
                                                        rowIdx++
                                                        const phaseMode = getGroupPhaseMode(items)
                                                        return (
                                                            <div key={item.id || `${row.disc}-${idx}`} style={{ display: 'flex', alignItems: 'baseline', gap: 8, padding: '3px 0', borderBottom: '1px solid #f0f0f0' }}>
                                                                <span style={{ fontSize: 10, color: '#bbb', width: 20, textAlign: 'right', flexShrink: 0 }}>{rowIdx}</span>
                                                                <span style={{ fontSize: 11, color: '#1a1a1a', flex: 1 }}>{item.name}</span>
                                                                {phaseMode === 'mixed' && item.phase && <PhasePill phase={item.phase} t={t} />}
                                                            </div>
                                                        )
                                                    })}
                                                </div>
                                            ) : null}
                                        </div>
                                    )
                                })}

                                {/* Non-percentage disciplines (e.g. 3D Services scope items) */}
                                {extraDiscs.map(d => {
                                    const items = discMap[d] || []
                                    if (items.length === 0) return null
                                    return (
                                        <div key={d} style={{ marginBottom: 14, pageBreakInside: 'avoid' }}>
                                            <div style={{
                                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                                background: ds(d).bg, border: `1px solid ${ds(d).border}`,
                                                borderRadius: 6, padding: '6px 10px', marginBottom: 6,
                                            }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                    <Chip disc={d} />
                                                    <span style={{ fontSize: 11, fontWeight: 700, color: ds(d).text }}>{d}</span>
                                                    {getGroupPhaseMode(items) === 'single' && <PhasePill phase={getDiscPhases(items)[0]} t={t} />}
                                                </div>
                                            </div>
                                            {items.length > 0 ? (
                                                <div style={{ paddingLeft: 8 }}>
                                                    {items.map((item, idx) => {
                                                        rowIdx++
                                                        const phaseMode = getGroupPhaseMode(items)
                                                        return (
                                                            <div key={item.id || `${d}-${idx}`} style={{ display: 'flex', alignItems: 'baseline', gap: 8, padding: '3px 0', borderBottom: '1px solid #f0f0f0' }}>
                                                                <span style={{ fontSize: 10, color: '#bbb', width: 20, textAlign: 'right', flexShrink: 0 }}>{rowIdx}</span>
                                                                <span style={{ fontSize: 11, color: '#1a1a1a', flex: 1 }}>{item.name}</span>
                                                                {phaseMode === 'mixed' && item.phase && <PhasePill phase={item.phase} t={t} />}
                                                            </div>
                                                        )
                                                    })}
                                                </div>
                                            ) : (
                                                <div style={{ paddingLeft: 8, fontSize: 10, color: '#aaa', fontStyle: 'italic' }}>
                                                    {t('admin.arch.no_deliverables')}
                                                </div>
                                            )}
                                        </div>
                                    )
                                })}
                            </>
                        )}

                        {/* Add-ons */}
                        {activeAddons.length > 0 && (
                            <div style={{ marginBottom: 14, pageBreakInside: 'avoid' }}>
                                <div style={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                    background: '#f3f4f6', border: '1px solid #d1d5db',
                                    borderRadius: 6, padding: '6px 10px', marginBottom: 6,
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <span style={{ background: '#f3f4f6', color: '#6b7280', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 9, fontWeight: 700, padding: '2px 6px', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{t('admin.arch.addon_label')}</span>
                                        <span style={{ fontSize: 11, fontWeight: 700, color: '#555' }}>{t('admin.arch.addons_label')}</span>
                                    </div>
                                </div>
                                <div style={{ paddingLeft: 8 }}>
                                    {activeAddons.map(addon => {
                                        const subItems = addonItemMap[addon.key] || []
                                        const def = addonDefs.find(d => d.key === addon.key)
                                        return (
                                            <div key={addon.key} style={{ padding: '3px 0', borderBottom: '1px solid #f0f0f0' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                    <span style={{ fontSize: 11, fontWeight: 700, color: '#555' }}>{getAddonLabel(def, t) || addon.label}</span>
                                                    <span style={{ fontSize: 11, fontWeight: 600, color: '#555' }}>${fmt(Number(addon.amount) || 0)}</span>
                                                </div>
                                                {subItems.length > 0 && (
                                                    <div style={{ paddingLeft: 12, marginTop: 2, marginBottom: 2 }}>
                                                        {subItems.map((name, idx) => (
                                                            <div key={idx} style={{ fontSize: 10, color: '#777', padding: '1px 0', display: 'flex', alignItems: 'baseline', gap: 6 }}>
                                                                <span style={{ color: '#bbb', fontSize: 8 }}>•</span>
                                                                <span>{name}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        )
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Total Contract */}
                        <div style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            background: '#1a1a1a', borderRadius: 6, padding: '8px 10px', marginTop: 8, marginBottom: 20,
                        }}>
                            <span style={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '1px', color: '#fff' }}>{t('admin.arch.preview_total_contract')}</span>
                            <span style={{ fontSize: 14, fontWeight: 900, color: BRAND }}>${fmt(totalContract)}</span>
                        </div>
                    </div>
                )}

                {/* ── Scope Detail (inc / exc) */}
                {(allInc.length > 0 || filteredExc.length > 0) && (
                    <div style={{ marginBottom: 20 }}>
                        <SectionHead title={t('admin.arch.scope_detail')} />
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
                            {allInc.length > 0 && (
                                <div>
                                    <div style={{ fontSize: 10, fontWeight: 800, color: '#16a34a', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 8 }}>{t('admin.arch.included_label')}</div>
                                    {allInc.map((b, i) => (
                                        <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 4, alignItems: 'flex-start', pageBreakInside: 'avoid' }}>
                                            <span style={{ color: '#16a34a', flexShrink: 0, fontSize: 11 }}>✔</span>
                                            <span style={{ fontSize: 11, color: '#333', lineHeight: 1.4 }}>{b}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {filteredExc.length > 0 && (
                                <div>
                                    <div style={{ fontSize: 10, fontWeight: 800, color: '#dc2626', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 8 }}>{t('admin.arch.not_included_label')}</div>
                                    {filteredExc.map((b, i) => (
                                        <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 4, alignItems: 'flex-start', pageBreakInside: 'avoid' }}>
                                            <span style={{ color: '#dc2626', flexShrink: 0, fontSize: 11 }}>•</span>
                                            <span style={{ fontSize: 11, color: '#666', lineHeight: 1.4 }}>{b}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* ── Important Notes */}
                {(() => {
                    const activeTermGroups = getActiveTermsGrouped(estimate)
                    if (activeTermGroups.length === 0) return null
                    return (
                        <div style={{ marginBottom: 20, marginTop: 8 }}>
                            <SectionHead title={t('admin.arch.terms_section_title')} />
                            {activeTermGroups.map((group, gi) => (
                                <div key={gi} style={{ marginBottom: 12 }}>
                                    {group.title && (
                                        <div style={{ fontSize: 10, fontWeight: 800, color: '#666', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 6 }}>{group.title}</div>
                                    )}
                                    {group.items.map((term, i) => (
                                        <div key={term.id || i} style={{ display: 'flex', gap: 8, marginBottom: 5, pageBreakInside: 'avoid' }}>
                                            <span style={{ color: BRAND, fontWeight: 900, flexShrink: 0 }}>•</span>
                                            <span style={{ fontSize: 11, color: '#333', lineHeight: 1.5, wordBreak: 'break-word', textAlign: 'justify' }}>{cleanTermText(term.text)}</span>
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </div>
                    )
                })()}

                {/* ── Payment Schedule */}
                {totalContract > 0 && paySchedule.length > 0 && (
                    <div style={{ marginBottom: 20 }}>
                        <SectionHead
                            title={t('admin.arch.payment_schedule')}
                            subtitle={!schedValid ? `⚠ ${t('admin.arch.schedule_incomplete', { pct: schedTotal })}` : undefined}
                        />
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead>
                                <tr>
                                    <th style={TH}>{t('admin.arch.milestone')}</th>
                                    {/* Pct column hidden per request — still used internally for amount calculation */}
                                    {/* <th style={{ ...TH, textAlign: 'right', width: 55 }}>{t('admin.arch.pct')}</th> */}
                                    <th style={{ ...TH, textAlign: 'right', width: 120 }}>{t('admin.arch.amount_due')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paySchedule.map((row, i) => {
                                    const amount = totalContract * ((Number(row.pct) || 0) / 100)
                                    return (
                                        <tr key={i} style={{ borderBottom: '1px solid #f0f0f0', pageBreakInside: 'avoid' }}>
                                            <td style={{ ...TD, color: '#333' }}>{row.label || `${t('admin.arch.payment_fallback')} ${i + 1}`}</td>
                                            {/* Pct cell hidden per request */}
                                            {/* <td style={{ ...TD, textAlign: 'right', color: '#888', fontWeight: 600 }}>{row.pct}%</td> */}
                                            <td style={{ ...TD, textAlign: 'right', fontWeight: 700, color: '#1a1a1a' }}>${fmt(amount)}</td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>

                        {/* ── What You Should Know (dynamic term) */}
                        {(() => {
                            const term = getWhatYouShouldKnowTerm(estimate)
                            const text = getWhatYouShouldKnowText(estimate)
                            if (!term || !text) return null
                            return (
                                <div style={{ marginTop: 20, pageBreakInside: 'avoid' }}>
                                    <p style={{ fontSize: 11, fontWeight: 700, color: '#1a1a1a', lineHeight: 1.6, margin: '0 0 10px' }}>
                                        {term.section_title || ''}
                                    </p>
                                    <p style={{ fontSize: 11, color: '#333', lineHeight: 1.6, margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', textAlign: 'justify' }}>
                                        {text}
                                    </p>
                                </div>
                            )
                        })()}
                    </div>
                )}

                {/* ── Additional Comments */}
                {estimate.additionalComments && estimate.additionalComments.trim() && (
                    <div style={{ marginBottom: 20 }}>
                        <SectionHead title={t('admin.arch.preview_additional_comments')} />
                        <div style={{ padding: '12px 14px', background: '#fafafa', border: '1px solid #e5e5e5', borderRadius: 6 }}>
                            <p style={{ fontSize: 11, color: '#333', lineHeight: 1.6, margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word', textAlign: 'justify' }}>{estimate.additionalComments}</p>
                        </div>
                    </div>
                )}

                {/* ── Preliminary User Info */}
                {isPrelim && (
                    <div style={{ marginBottom: 20, pageBreakInside: 'avoid', textAlign: 'center' }}>
                        {(() => {
                            const preparedBy = session?.user
                            const name = preparedBy?.user_metadata?.full_name || preparedBy?.email || ''
                            const email = preparedBy?.email || ''
                            const phone = getFooterOfficeInfo(estimate, paymentInfos).phone
                            return name ? (
                                <>
                                    <div style={{ fontSize: 11, fontWeight: 700, color: '#1a1a1a', marginBottom: 4 }}>{name}</div>
                                    <div style={{ fontSize: 10, fontStyle: 'italic', color: '#888', marginBottom: 4 }}>{t('admin.arch.sales_representative')}</div>
                                    {email && <div style={{ fontSize: 10, color: '#888', marginBottom: 4 }}>{email}</div>}
                                    {phone && <div style={{ fontSize: 10, color: '#888' }}>{phone}</div>}
                                </>
                            ) : null
                        })()}
                    </div>
                )}

                {/* ── Closing + Signatures (formal only) */}
                {!isPrelim && (
                    <>
                        <div style={{ marginBottom: 20, pageBreakInside: 'avoid' }}>
                            <div style={{ fontSize: 11, color: '#333', marginBottom: 10, lineHeight: 1.6, wordBreak: 'break-word', textAlign: 'justify' }}>
                                {t('admin.arch.new_closing_text')}
                            </div>
                            <div style={{ fontSize: 11, fontWeight: 700, color: '#1a1a1a', marginBottom: 14 }}>{t('admin.arch.sincerely')}</div>
                            {(() => {
                                const preparedBy = session?.user
                                const name = preparedBy?.user_metadata?.full_name || preparedBy?.email || ''
                                const email = preparedBy?.email || ''
                                const phone = getFooterOfficeInfo(estimate, paymentInfos).phone
                                return name ? (
                                    <div style={{ textAlign: 'center', marginBottom: 14 }}>
                                        <div style={{ fontSize: 11, fontWeight: 700, color: '#1a1a1a' }}>{name}</div>
                                        <div style={{ fontSize: 10, fontStyle: 'italic', color: '#888' }}>{t('admin.arch.sales_representative')}</div>
                                        <div style={{ fontSize: 10, color: '#888' }}>{email}</div>
                                        <div style={{ fontSize: 10, color: '#888' }}>{phone}</div>
                                    </div>
                                ) : null
                            })()}
                            {activeMethods.map((method, idx) => (
                                <div key={idx} style={{ marginBottom: 14 }}>
                                    <div style={{ fontSize: 11, color: '#333', lineHeight: 1.6 }}>
                                        <p style={{ fontWeight: 700, margin: '0 0 6px' }}>{t('admin.arch.remit_to')}</p>
                                        {(() => {
                                            const accountName = method.account_name || resolveArchOfficeInfo(estimate).companyName
                                            return accountName ? <p style={{ margin: 0 }}>{t('admin.arch.pm_name')} {accountName}</p> : null
                                        })()}
                                        {method.bank_name && <p style={{ margin: '2px 0 0' }}>{t('admin.arch.pm_bank')} {method.bank_name}</p>}
                                        {method.account_number && <p style={{ margin: '2px 0 0' }}>{t('admin.arch.pm_account')} {method.account_number}</p>}
                                        {method.routing_number && <p style={{ margin: '2px 0 0' }}>{t('admin.arch.pm_routing')} {method.routing_number}</p>}
                                        {method.email && <p style={{ margin: '2px 0 0' }}>{t('admin.arch.pm_email')} {method.email} {t('admin.arch.pm_zelle')}</p>}
                                        <p style={{ margin: '8px 0 0', fontStyle: 'italic', color: '#555' }}>
                                            {method.card_note || t('admin.arch.pm_card_note')}
                                        </p>
                                        <p style={{ margin: '4px 0 0', fontStyle: 'italic', color: '#555' }}>
                                            {method.check_note || t('admin.arch.pm_check_note')}
                                        </p>
                                    </div>
                                </div>
                            ))}
                            {activeMethods.length === 0 && (
                                <div style={{ marginBottom: 14 }}>
                                    <div style={{ fontSize: 11, color: '#333', lineHeight: 1.6 }}>
                                        <p style={{ fontWeight: 700, margin: '0 0 6px' }}>{t('admin.arch.remit_to')}</p>
                                        {(() => {
                                            const accountName = resolveArchOfficeInfo(estimate).companyName
                                            return accountName ? <p style={{ margin: 0 }}>{t('admin.arch.pm_name')} {accountName}</p> : null
                                        })()}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* HIDDEN: Company signature — client-only signing per request */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 40, borderTop: `2px solid ${BRAND}`, paddingTop: 20, marginBottom: 28, pageBreakInside: 'avoid' }}>
                            {[
                                { party: t('admin.arch.sig_client'), fields: [t('admin.arch.sig_client_signature')] },
                                /* hidden company column */ { party: `${(() => { const o = getFooterOfficeInfo(estimate, paymentInfos); return o.companyName })()}`, fields: [t('admin.arch.sig_authorized'), t('admin.arch.sig_title'), t('admin.arch.sig_date')] },
                            ].map((col, ci) => (
                                ci === 0 ? (
                                    <div key={ci}>
                                        <div style={{ fontSize: 9, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 14 }}>{col.party}</div>
                                        {col.fields.map(field => (
                                            <div key={field} style={{ marginBottom: 16 }}>
                                                <div style={{ fontSize: 10, fontWeight: 700, color: '#aaa', marginBottom: 6 }}>{field}</div>
                                                <div style={{ borderBottom: '1px solid #bbb', height: 24 }} />
                                            </div>
                                        ))}
                                    </div>
                                ) : null
                            ))}
                        </div>
                    </>
                )}

                {/* ── Company footer line */}
                {(() => {
                    const office = getFooterOfficeInfo(estimate, paymentInfos)
                    return (
                        <div style={{ borderTop: '1px solid #e5e5e5', paddingTop: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                            <div>
                                <p style={{ fontSize: 10, fontWeight: 800, color: '#1a1a1a', textTransform: 'uppercase', letterSpacing: '0.5px', margin: 0 }}>{office.companyName}</p>
                                <p style={{ fontSize: 10, color: '#888', margin: '2px 0 0' }}>{office.address}</p>
                            </div>
                            <div style={{ textAlign: 'right' }}>
                                <p style={{ fontSize: 10, color: '#888', margin: 0 }}>{COMPANY.email}</p>
                                <p style={{ fontSize: 10, color: '#888', margin: '2px 0 0' }}>{COMPANY.website}</p>
                            </div>
                        </div>
                    )
                })()}

            </div>{/* /content */}

            {/* ── Footer image */}
            <img src="/footer.png" alt="" style={{ width: '100%', display: 'block' }} />

            {/* ── Page number (single marker at end of preview, bottom-right) */}
            <div
                style={{
                    position: 'absolute',
                    right: 36,
                    bottom: 12,
                    pointerEvents: 'none',
                }}
            >
                <span style={{ fontSize: 9, color: '#bbb', fontWeight: 600 }}>
                    {t('admin.arch.page_number', { current: pageCount, total: pageCount })}
                </span>
            </div>
        </div>
    )
}

// ── ArchEstimatePreview (exported wrapper) ────────────────────────────────────
export default function ArchEstimatePreview({ estimate, mode = 'formal', difficultyLevels, paymentInfo, session, addonDefs = [] }) {
    const { t, i18n } = useTranslation()
    const [exporting, setExporting] = useState(false)
    const [pdfLang, setPdfLang] = useState(i18n.language || 'en')
    const isPrelim  = mode === 'preliminary'

    const pdfT = useCallback((key, options) => i18n.t(key, { lng: pdfLang, ...options }), [i18n, pdfLang])

    const preparedBy = {
        name: session?.user?.user_metadata?.full_name || session?.user?.email || '',
        email: session?.user?.email || '',
    }

    const buildPDF = async (t, locale = 'en-US', preparedBy) => {
        const pdf   = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'letter' })
        const pageW = pdf.internal.pageSize.getWidth()
        const pageH = pdf.internal.pageSize.getHeight()
        const M = 36
        const loadImg = (src) => new Promise((res, rej) => {
            const img = new Image()
            // Same-origin images; avoid CORS tainting that blocks canvas readback
            if (new URL(src, window.location.href).origin !== window.location.origin) {
                img.crossOrigin = 'anonymous'
            }
            img.onload = () => {
                try {
                    const c = document.createElement('canvas')
                    c.width = img.naturalWidth; c.height = img.naturalHeight
                    c.getContext('2d').drawImage(img, 0, 0)
                    res({ data: c.toDataURL('image/png'), aspect: img.naturalHeight / img.naturalWidth })
                } catch (err) {
                    rej(new Error('Canvas taint loading ' + src + ': ' + err.message))
                }
            }
            img.onerror = () => rej(new Error('Failed to load image: ' + src))
            img.src = src
        })
        const [hdr, ftr] = await Promise.all([
            loadImg(`${window.location.origin}/header.png`),
            loadImg(`${window.location.origin}/footer.png`),
        ])
        const hdrH = hdr.aspect * pageW
        const ftrH = ftr.aspect * pageW
        const contentTop = hdrH + 18
        const contentBottom = pageH - ftrH - 18

        let y = contentTop
        function drawHdr() { pdf.addImage(hdr.data, 'PNG', 0, 0, pageW, hdrH) }
        function drawFtr() { pdf.addImage(ftr.data, 'PNG', 0, pageH - ftrH, pageW, ftrH) }
        function ensure(h) { if (y + h > contentBottom) { pdf.addPage(); drawHdr(); drawFtr(); y = contentTop } }
        function pdfFont(size, style = 'normal', color = [26, 26, 26]) {
            pdf.setFontSize(size)
            pdf.setFont('helvetica', style)
            pdf.setTextColor(...color)
        }
        function addAcroTextField({ name, x, yTop, w, h, fontSize = 11, align = 0, maxLength = 0, value = '' }) {
            const field = new AcroFormTextField()
            field.fieldName = name
            field.x = x
            field.y = yTop  // jsPDF handles PDF coordinate conversion internally
            field.width = w
            field.height = h
            field.fontSize = fontSize
            field.textAlign = align
            if (maxLength > 0) field.maxLength = maxLength
            if (value) field.value = value
            pdf.addField(field)
        }
        function sectionHead(title, subtitle = null, subtitleBold = null, matchTitle = false) {
            ensure(24)
            pdfFont(9, 'bold')
            pdf.text(title.toUpperCase(), M, y)
            if (subtitleBold || subtitle) {
                if (matchTitle) {
                    pdfFont(9, 'bold')
                    const fullText = ((subtitleBold || '') + (subtitle ? ' ' + subtitle : '')).toUpperCase()
                    pdf.text(fullText, pageW - M, y, { align: 'right' })
                } else {
                    // Calculate normal subtitle width first (in normal font)
                    let normalWidth = 0
                    if (subtitle) {
                        pdfFont(8, 'normal', [136,136,136])
                        normalWidth = pdf.getTextWidth(' ' + subtitle)
                    }
                    if (subtitleBold) {
                        pdfFont(8, 'bold', [85,85,85])
                        pdf.text(subtitleBold, pageW - M - normalWidth, y, { align: 'right' })
                    }
                    if (subtitle) {
                        pdfFont(8, 'normal', [136,136,136])
                        pdf.text(subtitle, pageW - M, y, { align: 'right' })
                    }
                }
            }
            y += 12
            pdf.setDrawColor(26,26,26); pdf.setLineWidth(1.5); pdf.line(M, y, pageW - M, y)
            y += 10
        }

        const WRAP_PAD = 2 // pt of right-side breathing room so text never butts the margin
        // Hard-break a single token that is wider than maxWidth, using binary search.
        function hardBreakToken(token, maxWidth) {
            const segs = []
            while (pdf.getTextWidth(token) > maxWidth) {
                let lo = 1, hi = token.length, fit = 1
                while (lo <= hi) {
                    const mid = (lo + hi) >> 1
                    if (pdf.getTextWidth(token.slice(0, mid)) <= maxWidth) { fit = mid; lo = mid + 1 }
                    else hi = mid - 1
                }
                segs.push(token.slice(0, fit))
                token = token.slice(fit)
            }
            return { segs, rest: token }
        }
        function wrapToWidth(text, maxWidthRaw) {
            // Returns line objects: { text, last, broken }
            //   last   = last visual line of its paragraph (never justify)
            //   broken = produced by hard-breaking a too-long word (never justify)
            const maxWidth = maxWidthRaw - WRAP_PAD
            const out = []
            const paragraphs = String(text).split(/\r?\n/)
            for (const para of paragraphs) {
                if (para.trim() === '') { out.push({ text: '', last: true, broken: false }); continue }
                const tokens = para.match(/\s+|\S+/g) || []
                const lines = [] // { text, broken }
                let cur = ''
                for (let tok of tokens) {
                    if (/^\s+$/.test(tok)) {
                        if (cur === '') continue
                        if (pdf.getTextWidth(cur + tok) <= maxWidth) cur += tok
                        continue
                    }
                    if (pdf.getTextWidth(cur + tok) <= maxWidth) { cur += tok; continue }
                    if (cur !== '') { lines.push({ text: cur.replace(/\s+$/, ''), broken: false }); cur = '' }
                    if (pdf.getTextWidth(tok) > maxWidth) {
                        const { segs, rest } = hardBreakToken(tok, maxWidth)
                        for (const s of segs) lines.push({ text: s, broken: true })
                        tok = rest
                    }
                    cur = tok
                }
                if (cur !== '') lines.push({ text: cur.replace(/\s+$/, ''), broken: false })
                if (lines.length === 0) lines.push({ text: '', broken: false })
                for (let i = 0; i < lines.length; i++) {
                    out.push({ text: lines[i].text, broken: lines[i].broken, last: i === lines.length - 1 })
                }
            }
            return out
        }
        function drawJustifiedLine(text, x, baseY, maxWidth) {
            const words = text.trim().split(/\s+/).filter(Boolean)
            if (words.length <= 1) { pdf.text(text.trim(), x, baseY); return }
            const wordsW = words.reduce((s, w) => s + pdf.getTextWidth(w), 0)
            const gap = (maxWidth - wordsW) / (words.length - 1)
            if (gap <= 0) { pdf.text(text.trim(), x, baseY); return }
            let cx = x
            for (let i = 0; i < words.length; i++) {
                pdf.text(words[i], cx, baseY)
                cx += pdf.getTextWidth(words[i]) + gap
            }
        }

        // ── Data prep (exact match to preview)
        const isPrelimPDF  = mode === 'preliminary'
        const isAddAltPDF  = ADD_ALT_PROJECT_TYPES.has(estimate.projectType)
        const allScopeItems = estimate.scopeItems || []
        const totalContract = Number(estimate.totalContract) || 0
        const salePrice     = Number(estimate.salePrice) || 0
        const paySchedule   = estimate.cuotas || []
        const schedTotal    = paySchedule.reduce((s, c) => s + (Number(c.pct) || 0), 0)
        const schedValid    = Math.abs(schedTotal - 100) < 0.01
        // For Add/Alt/Rem projects civil engineering is never part of the deliverable set.
        const allowedDiscs  = (ADD_ALT_PROJECT_TYPES.has(estimate.projectType)
            ? (PDF_DISCIPLINE_SCOPE_ALLOWED[estimate.disciplineScope] || PDF_DISCIPLINE_SCOPE_ALLOWED.full).filter(d => d !== 'CIVIL')
            : (PDF_DISCIPLINE_SCOPE_ALLOWED[estimate.disciplineScope] || PDF_DISCIPLINE_SCOPE_ALLOWED.full))
        const gatedItemsRawPDF = applyAddonGatedFilter(
            allScopeItems.filter(i => allowedDiscs.includes(normalizeDiscipline(i.discipline))),
            estimate.addons,
            addonDefs
        )
        // Filter out certification items whose COM/RES suffix doesn't match the project type
        const ptPrefixPDF = getProjectTypeComResPrefix(estimate.projectType)
        const gatedItemsPDF = gatedItemsRawPDF.filter(item => {
            const name = (item.name || '').trim()
            const comMatch = /[\s–—-]+COM\s*$/i.test(name)
            const resMatch = /[\s–—-]+RES\s*$/i.test(name)
            if (!comMatch && !resMatch) return true
            if (ptPrefixPDF === 'com') return comMatch
            if (ptPrefixPDF === 'res') return resMatch
            return false
        })
        // Separate add-on items from discipline deliverables — add-on items appear only in ADD-ONS section
        const scopeItems    = gatedItemsPDF.filter(i => !isAddonItem(i, addonDefs))
        const addonItemsPDF = gatedItemsPDF.filter(i => isAddonItem(i, addonDefs))

        const paymentInfosPDF = Array.isArray(paymentInfo) ? paymentInfo : paymentInfo ? [paymentInfo] : []
        const estimateStatePDF = getOfficeState(estimate)
        const activeMethods = paymentInfosPDF
            .filter(m => m.is_active !== false)
            .filter(m => m.office_key === estimateStatePDF || m.office_key === 'both')
            .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))

        const discMap = {}
        for (const item of scopeItems) {
            const d = normalizeDiscipline(item.discipline)
            if (!discMap[d]) discMap[d] = []
            discMap[d].push(item)
        }
        const allInc = [], allExc = []
        const seenInc = new Set(), seenExc = new Set()
        for (const item of scopeItems) {
            for (const b of (item.inc || [])) { if (!seenInc.has(b)) { seenInc.add(b); allInc.push(b) } }
            for (const b of (item.exc || [])) { if (!seenExc.has(b)) { seenExc.add(b); allExc.push(b) } }
        }
        const filteredExc = allExc.filter(e => !allInc.some(i => hasTokenOverlap(e, i)))
        const activeDiscs  = DISC_BREAKDOWN(estimate, t).filter(r => Number(estimate[r.key] || 0) > 0 && salePrice > 0)
        const activeAddons = (estimate.addons || [])
            .filter(a => a.enabled === true)
            .sort((a, b) => {
                const defA = addonDefs.find(d => d.key === a.key)
                const defB = addonDefs.find(d => d.key === b.key)
                return (defA?.sort_order || 0) - (defB?.sort_order || 0)
            })

        // Map each enabled add-on to its related deliverable names for sub-list rendering
        const addonItemMapPDF = {}
        for (const addon of activeAddons) {
            const def = addonDefs.find(d => d.key === addon.key)
            if (def) {
                addonItemMapPDF[addon.key] = addonItemsPDF
                    .filter(i => evaluateAddonRule(i, def))
                    .map(i => i.name)
            }
        }

        // Non-percentage disciplines that have scope items (e.g. 3D Services)
        const activeDiscSetPDF = new Set(activeDiscs.map(r => r.disc))
        const seenDiscsPDF = Object.keys(discMap)
        const discOrderPDF = [
            ...DISC_ORDER.filter(d => seenDiscsPDF.includes(d)),
            ...seenDiscsPDF.filter(d => !DISC_ORDER.includes(d)).sort(),
        ]
        const extraDiscsPDF = discOrderPDF.filter(d => !activeDiscSetPDF.has(d) && (discMap[d] || []).length > 0)

        const officeClosing = getFooterOfficeInfo(estimate, paymentInfosPDF)

        drawHdr()
        drawFtr()
        // ── 5.1 Preliminary banner
        if (isPrelimPDF) {
            ensure(30)
            const h = hex(BRAND_BG), b = hex(BRAND_BD), tc = hex('#92400e')
            pdf.setFillColor(h.r, h.g, h.b)
            pdf.setDrawColor(b.r, b.g, b.b)
            pdf.roundedRect(M, y, pageW - M*2, 22, 3, 3, 'FD')
            pdfFont(9, 'bold', [tc.r, tc.g, tc.b])
            pdf.text(t('admin.arch.preliminary_banner'), M + 10, y + 14)
            y += 30
        }

        // ── 5.2 Title bar
        ensure(estimate.projectDescription ? 52 : 42)
        const br = hex(BRAND)
        pdf.setFillColor(br.r, br.g, br.b)
        pdf.rect(M, y, 4, 34, 'F')
        pdfFont(14, 'bold')
        const titleText = isPrelimPDF ? t('admin.arch.preliminary_title') : t('admin.arch.proposal_title_design_services')
        pdf.text(titleText, M + 14, y + 16)
        pdfFont(8, 'normal', [136,136,136])
        let meta = (isPrelimPDF ? t('admin.arch.meta_estimate') : t('admin.arch.meta_proposal')) + ' #' + (estimate.number || '—') + '   ·   ' + t('admin.arch.meta_issued') + ' ' + fmtDate(estimate.date, locale)
        if (!isPrelimPDF) meta += '   ·   ' + t('admin.arch.meta_valid_through') + ' ' + validUntil(estimate.date, locale)
        pdf.text(meta, M + 14, y + 28)
        if (estimate.projectDescription) {
            pdfFont(8, 'italic', [85,85,85])
            pdf.text(estimate.projectDescription, M + 14, y + 40)
        }
        // Status hidden from PDF — internal field, not client-facing
        // pdfFont(8, 'bold', [170,170,170])
        // pdf.text(t('admin.arch.meta_status'), pageW - M, y + 12, { align: 'right' })
        // pdfFont(10, 'bold')
        // pdf.text(t('admin.arch.status_' + (estimate.status || 'draft')), pageW - M, y + 24, { align: 'right' })
        y += estimate.projectDescription ? 52 : 42
        y += 14

        // ── 5.3 Client grid
        sectionHead(t('admin.arch.client_project_info'))
        const cellW = (pageW - M*2) / 2
        const cellH = 24
        const gridData = [
            [t('admin.arch.preview_client_name'), estimate.client || '—', t('admin.arch.preview_project_address'), estimate.address || '—'],
            [t('admin.arch.preview_phone'), estimate.phone || '—', t('admin.arch.preview_email'), estimate.email || '—'],
            [t('admin.arch.preview_project_type'), estimate.projectType ? (t('admin.arch.ptype_' + estimate.projectType) || estimate.projectType) : '—', t('admin.arch.preview_area'), Number(estimate.sqft || 0).toLocaleString() + ' ' + t('admin.arch.area_sqft')],
        ]
        ensure(cellH * 3 + 10)
        for (let row = 0; row < 3; row++) {
            const bg = (row % 2 === 0) ? { r: 249, g: 250, b: 251 } : { r: 255, g: 255, b: 255 }
            const rowY = y + row * cellH
            pdf.setFillColor(bg.r, bg.g, bg.b)
            pdf.rect(M, rowY, cellW * 2, cellH, 'F')
            pdf.setDrawColor(240, 240, 240); pdf.setLineWidth(0.5)
            pdf.line(M, rowY + cellH, M + cellW * 2, rowY + cellH)
            if (gridData[row][0]) {
                pdfFont(8, 'bold', [170, 170, 170])
                pdf.text(gridData[row][0].toUpperCase(), M + 8, rowY + 7)
                pdfFont(10, 'normal')
                pdf.text(String(gridData[row][1]), M + 8, rowY + 17)
            }
            if (gridData[row][2]) {
                if (row === 2 && isAddAltPDF) {
                    // Add/Alt: 3 mini columns in the Area cell
                    const addAltCols = [
                        { label: t('admin.arch.add_alt_existente'), sqft: estimate.existSqft, diff: estimate.existDiff },
                        { label: t('admin.arch.add_alt_remodel'),   sqft: estimate.remSqft,   diff: estimate.remDiff },
                        { label: t('admin.arch.add_alt_adicion'),    sqft: estimate.addSqft,   diff: estimate.addDiff },
                    ].filter(col => Number(col.sqft || 0) > 0)
                    const miniW = cellW / Math.max(addAltCols.length, 1)
                    pdfFont(7, 'bold', [170, 170, 170])
                    pdf.text(gridData[row][2].toUpperCase(), M + cellW + 8, rowY + 6)
                    pdfFont(8, 'normal')
                    addAltCols.forEach((col, i) => {
                        const cx = M + cellW + 8 + i * miniW
                        pdfFont(6, 'bold', [170, 170, 170])
                        pdf.text(col.label.toUpperCase(), cx, rowY + 12)
                        pdfFont(8, 'normal')
                        pdf.text(`${Number(col.sqft).toLocaleString()} ${t('admin.arch.area_sqft')}`, cx, rowY + 20)
                    })
                } else {
                    pdfFont(8, 'bold', [170, 170, 170])
                    pdf.text(gridData[row][2].toUpperCase(), M + cellW + 8, rowY + 7)
                    pdfFont(10, 'normal')
                    pdf.text(String(gridData[row][3]), M + cellW + 8, rowY + 17)
                }
            }
        }
        y += cellH * 3 + 14

        // ── 5.3b Intro paragraph
        if (!isPrelimPDF) {
            const introText = t('admin.arch.intro_text', { address: estimate.address || '—' })
            pdfFont(10, 'italic', [85, 85, 85])
            pdf.setLineHeightFactor(1.3)
            const introLines = wrapToWidth(introText, pageW - M * 2)
            const introLH = pdf.getLineHeight()
            ensure(introLH + introLines.length * introLH)
            pdf.setCharSpace(0)
            let iy = y
            for (const ln of introLines) {
                iy += introLH
                if (!ln.last && !ln.broken) drawJustifiedLine(ln.text, M, iy, pageW - M * 2 - WRAP_PAD)
                else pdf.text(ln.text, M, iy)
            }
            pdf.setLineHeightFactor(1.15)
            y += introLH + introLines.length * introLH + 6
        }

        // ── 5.3c Preliminary Intro paragraph
        if (isPrelimPDF) {
            const greeting = getTimeGreeting(t)
            const prelimIntro = t('admin.arch.preliminary_intro_greeting', { address: estimate.address || '—' })
            pdfFont(10, 'normal', [51, 51, 51])
            pdf.setLineHeightFactor(1.3)
            // Greeting line (left-aligned)
            const greetLH = pdf.getLineHeight()
            ensure(greetLH)
            pdf.text(greeting, M, y + greetLH)
            y += greetLH + 4
            // Body paragraph (justified)
            const prelimLines = wrapToWidth(prelimIntro, pageW - M * 2)
            const prelimLH = pdf.getLineHeight()
            ensure(prelimLH + prelimLines.length * prelimLH)
            pdf.setCharSpace(0)
            let py = y
            for (const ln of prelimLines) {
                py += prelimLH
                if (!ln.last && !ln.broken) drawJustifiedLine(ln.text, M, py, pageW - M * 2 - WRAP_PAD)
                else pdf.text(ln.text, M, py)
            }
            pdf.setLineHeightFactor(1.15)
            y += prelimLH + prelimLines.length * prelimLH + 10
        }

        // ── 5.4 Scope of Work
        if (estimate.notes && estimate.notes.trim()) {
            const tfValue = estimate.timeFrameValue && estimate.timeFrameUnit ? formatTimeFrame(estimate.timeFrameValue, estimate.timeFrameUnit, t) : null
            sectionHead(t('admin.arch.preview_scope_of_work'), tfValue, t('admin.arch.estimated_completion') + ':', true)
            const noteTextW = pageW - M*2 - 32
            const noteText = estimate.notes.trim()
                .replace(/^[\u2022\u2023\u25E6\u2043\u2219\u25AA\u25CF\*]+\s*/gm, '')
                .replace(/[\u00AD\u200B\u200C\u200D\u200E\u200F\u2028\u2029\uFEFF\u2060]/g, '')
                .replace(/\u00A0/g, ' ')
            pdfFont(10, 'normal', [51, 51, 51])
            pdf.setLineHeightFactor(1.3)
            const noteLines = wrapToWidth(noteText, noteTextW)
            const noteLH = pdf.getLineHeight()
            const boxH = 14 + noteLines.length * noteLH + 4
            ensure(boxH + 4)
            pdf.setFillColor(250, 250, 250)
            pdf.setDrawColor(229, 229, 229)
            pdf.roundedRect(M, y, pageW - M*2, boxH, 3, 3, 'FD')
            pdfFont(10, 'normal', [51, 51, 51])
            pdf.setCharSpace(0)
            let ny = y + 14 - noteLH
            for (const ln of noteLines) {
                ny += noteLH
                if (!ln.last && !ln.broken) drawJustifiedLine(ln.text, M + 12, ny, noteTextW - WRAP_PAD)
                else pdf.text(ln.text, M + 12, ny)
            }
            pdf.setLineHeightFactor(1.15)
            y += boxH + 14
        }

        // ── 5.5 Deliverables & Fee Allocation
        if (activeDiscs.length > 0 || activeAddons.length > 0) {
            sectionHead(t('admin.arch.deliverables_fee'))
            if (isPrelimPDF) {
                // Preliminary: bold discipline+price header, normal names below (matches HTML preview)
                for (const row of activeDiscs) {
                    const items = discMap[row.disc] || []
                    const pct = Number(estimate[row.key] || 0)
                    const base = estimate.useCivilSplit && (row.disc === 'CIVIL' || row.disc === 'CIVIL_ADMIN')
                        ? (estimate.civilPrice || 0)
                        : estimate.useCivilSplit
                            ? (estimate.mainPrice || 0)
                            : salePrice
                    const amt = (base * pct) / 100
                    const discPhaseMode = getGroupPhaseMode(items)
                    const phaseTag = discPhaseMode === 'single'
                        ? ' · ' + (getDiscPhases(items)[0] === 'cds' ? t('admin.arch.phase_cds_short') : t('admin.arch.phase_design_short'))
                        : ''
                    const names = discPhaseMode === 'mixed'
                        ? items.map(it => it.name + (it.phase ? ' (' + (it.phase === 'cds' ? t('admin.arch.phase_cds_short') : t('admin.arch.phase_design_short')) + ')' : '')).join(', ')
                        : items.map(it => it.name).join(', ')
                    const discLine = row.disc + ': $' + fmt(amt) + phaseTag + (names ? ' - ' + names : '')
                    pdfFont(10, 'normal', [26, 26, 26])
                    const dl = wrapToWidth(discLine, pageW - M * 2)
                    const h = 14 * dl.length
                    ensure(h)
                    let dy = y + 12 - 14
                    for (const ln of dl) {
                        dy += 14
                        if (!ln.last && !ln.broken) drawJustifiedLine(ln.text, M + 8, dy, pageW - M * 2 - WRAP_PAD)
                        else pdf.text(ln.text, M + 8, dy)
                    }
                    y += h + 8
                }
                for (const d of extraDiscsPDF) {
                    const items = discMap[d] || []
                    if (items.length === 0) continue
                    const extraPhaseMode = getGroupPhaseMode(items)
                    const extraPhaseTag = extraPhaseMode === 'single'
                        ? ' · ' + (getDiscPhases(items)[0] === 'cds' ? t('admin.arch.phase_cds_short') : t('admin.arch.phase_design_short'))
                        : ''
                    const names = extraPhaseMode === 'mixed'
                        ? items.map(it => it.name + (it.phase ? ' (' + (it.phase === 'cds' ? t('admin.arch.phase_cds_short') : t('admin.arch.phase_design_short')) + ')' : '')).join(', ')
                        : items.map(it => it.name).join(', ')
                    const discLine = d + ': ' + extraPhaseTag + ' ' + names
                    pdfFont(10, 'normal', [26, 26, 26])
                    const dl = wrapToWidth(discLine, pageW - M * 2)
                    const h = 14 * dl.length
                    ensure(h)
                    let dy = y + 12 - 14
                    for (const ln of dl) {
                        dy += 14
                        if (!ln.last && !ln.broken) drawJustifiedLine(ln.text, M + 8, dy, pageW - M * 2 - WRAP_PAD)
                        else pdf.text(ln.text, M + 8, dy)
                    }
                    y += h + 8
                }
                y += 6
            } else {
                let rowIdx = 0
                for (const row of activeDiscs) {
                    const pct = Number(estimate[row.key] || 0)
                    const base = estimate.useCivilSplit && (row.disc === 'CIVIL' || row.disc === 'CIVIL_ADMIN')
                        ? (estimate.civilPrice || 0)
                        : estimate.useCivilSplit
                            ? (estimate.mainPrice || 0)
                            : salePrice
                    const amt = (base * pct) / 100
                    const items = discMap[row.disc] || []
                    let itemH = 0
                    pdfFont(10, 'normal')
                    for (const it of items) {
                        const nl = pdf.splitTextToSize(it.name, pageW - M*2 - 40).length
                        itemH += 14 * nl
                    }
                    const blockH = 20 + 8 + itemH + 10
                    ensure(blockH)
                    const s = ds(row.disc)
                    const rgb = hex(s.bg), brgb = hex(s.border), trgb = hex(s.text)
                    pdf.setFillColor(rgb.r, rgb.g, rgb.b)
                    pdf.setDrawColor(brgb.r, brgb.g, brgb.b)
                    pdf.roundedRect(M, y, pageW - M*2, 20, 3, 3, 'FD')
                    pdfFont(8, 'bold', [trgb.r, trgb.g, trgb.b])
                    pdf.text(row.disc, M + 8, y + 14)
                    pdfFont(10, 'bold', [trgb.r, trgb.g, trgb.b])
                    pdf.text(row.name, M + 60, y + 14)
                    // Phase pill at group level if single phase
                    const discPhaseMode = getGroupPhaseMode(items)
                    if (discPhaseMode === 'single') {
                        pdfFont(10, 'bold', [trgb.r, trgb.g, trgb.b])
                        const nameW = pdf.getTextWidth(row.name)
                        drawPhasePillPDF(pdf, getDiscPhases(items)[0], M + 60 + nameW + 4, y + 14, t)
                    }
                    pdfFont(11, 'bold')
                    pdf.text('$' + fmt(amt), pageW - M - 8, y + 14, { align: 'right' })
                    y += 26
                    if (items.length > 0) {
                        for (const item of items) {
                            rowIdx++
                            pdfFont(10, 'normal')
                            const nl = pdf.splitTextToSize(item.name, pageW - M*2 - 40)
                            const h = 14 * nl.length
                            ensure(h)
                            pdfFont(9, 'normal', [187, 187, 187])
                            pdf.text(String(rowIdx), M + 20, y + 10, { align: 'right' })
                            pdfFont(10, 'normal')
                            for (let li = 0; li < nl.length; li++) {
                                pdf.text(nl[li], M + 28, y + 10 + li * 12)
                            }
                            // Phase pill at item level if mixed phases
                            if (discPhaseMode === 'mixed' && item.phase) {
                                pdfFont(10, 'normal')
                                const itemW = pdf.getTextWidth(nl[nl.length - 1])
                                drawPhasePillPDF(pdf, item.phase, M + 28 + itemW + 4, y + 10, t)
                            }
                            pdf.setDrawColor(240, 240, 240); pdf.setLineWidth(0.5)
                            pdf.line(M + 8, y + h, pageW - M, y + h)
                            y += h + 2
                        }
                    } else if (row.disc !== 'ADMIN' && row.disc !== 'CIVIL_ADMIN') {
                        ensure(18)
                        pdfFont(9, 'italic', [170, 170, 170])
                        pdf.text(t('admin.arch.no_deliverables'), M + 12, y + 12)
                        y += 18
                    }
                    y += 6
                }

                // Non-percentage disciplines (e.g. 3D Services scope items)
                for (const d of extraDiscsPDF) {
                    const items = discMap[d] || []
                    if (items.length === 0) continue
                    let itemH = 0
                    pdfFont(10, 'normal')
                    for (const it of items) {
                        const nl = pdf.splitTextToSize(it.name, pageW - M*2 - 40).length
                        itemH += 14 * nl
                    }
                    const blockH = 20 + 8 + itemH + 10
                    ensure(blockH)
                    const s = ds(d)
                    const rgb = hex(s.bg), brgb = hex(s.border), trgb = hex(s.text)
                    pdf.setFillColor(rgb.r, rgb.g, rgb.b)
                    pdf.setDrawColor(brgb.r, brgb.g, brgb.b)
                    pdf.roundedRect(M, y, pageW - M*2, 20, 3, 3, 'FD')
                    pdfFont(8, 'bold', [trgb.r, trgb.g, trgb.b])
                    pdf.text(d, M + 8, y + 14)
                    pdfFont(10, 'bold', [trgb.r, trgb.g, trgb.b])
                    pdf.text(d, M + 60, y + 14)
                    // Phase pill at group level if single phase
                    const extraPhaseMode = getGroupPhaseMode(items)
                    if (extraPhaseMode === 'single') {
                        pdfFont(10, 'bold', [trgb.r, trgb.g, trgb.b])
                        const nameW = pdf.getTextWidth(d)
                        drawPhasePillPDF(pdf, getDiscPhases(items)[0], M + 60 + nameW + 4, y + 14, t)
                    }
                    y += 26
                    for (const item of items) {
                        rowIdx++
                        pdfFont(10, 'normal')
                        const nl = pdf.splitTextToSize(item.name, pageW - M*2 - 40)
                        const h = 14 * nl.length
                        ensure(h)
                        pdfFont(9, 'normal', [187, 187, 187])
                        pdf.text(String(rowIdx), M + 20, y + 10, { align: 'right' })
                        pdfFont(10, 'normal')
                        for (let li = 0; li < nl.length; li++) {
                            pdf.text(nl[li], M + 28, y + 10 + li * 12)
                        }
                        // Phase pill at item level if mixed phases
                        if (extraPhaseMode === 'mixed' && item.phase) {
                            pdfFont(10, 'normal')
                            const itemW = pdf.getTextWidth(nl[nl.length - 1])
                            drawPhasePillPDF(pdf, item.phase, M + 28 + itemW + 4, y + 10, t)
                        }
                        pdf.setDrawColor(240, 240, 240); pdf.setLineWidth(0.5)
                        pdf.line(M + 8, y + h, pageW - M, y + h)
                        y += h + 2
                    }
                    y += 6
                }
            }

            // Add-ons
            if (activeAddons.length > 0) {
                // Calculate total height: header + each addon row + sub-items
                let totalAddonH = 20 + 6
                for (const addon of activeAddons) {
                    totalAddonH += 16
                    const subItems = addonItemMapPDF[addon.key] || []
                    for (const name of subItems) {
                        const nl = pdf.splitTextToSize(name, pageW - M*2 - 40).length
                        totalAddonH += 12 * nl
                    }
                    if (subItems.length > 0) totalAddonH += 4
                }
                ensure(totalAddonH)
                pdf.setFillColor(243, 244, 246)
                pdf.setDrawColor(209, 213, 219)
                pdf.roundedRect(M, y, pageW - M*2, 20, 3, 3, 'FD')
                pdfFont(8, 'bold', [107, 114, 128])
                pdf.text(t('admin.arch.addon_label'), M + 8, y + 14)
                pdfFont(10, 'bold', [85, 85, 85])
                pdf.text(t('admin.arch.addons_label'), M + 60, y + 14)
                y += 26
                for (const addon of activeAddons) {
                    ensure(16)
                    pdfFont(10, 'bold', [85, 85, 85])
                    const defPDF = addonDefs.find(d => d.key === addon.key)
                    pdf.text(getAddonLabel(defPDF, t) || addon.label, M + 12, y + 10)
                    pdfFont(10, 'bold', [85, 85, 85])
                    pdf.text('$' + fmt(Number(addon.amount) || 0), pageW - M - 8, y + 10, { align: 'right' })
                    y += 14
                    const subItems = addonItemMapPDF[addon.key] || []
                    if (subItems.length > 0) {
                        for (const name of subItems) {
                            const nl = pdf.splitTextToSize(name, pageW - M*2 - 40)
                            const h = 12 * nl.length
                            ensure(h)
                            pdfFont(9, 'normal', [170, 170, 170])
                            pdf.text('•', M + 18, y + 9)
                            pdfFont(9, 'normal', [119, 119, 119])
                            for (let li = 0; li < nl.length; li++) {
                                pdf.text(nl[li], M + 26, y + 9 + li * 11)
                            }
                            y += h + 2
                        }
                        y += 2
                    }
                    pdf.setDrawColor(240, 240, 240); pdf.setLineWidth(0.5)
                    pdf.line(M + 8, y, pageW - M, y)
                    y += 4
                }
            }

            // Total Contract bar
            ensure(28)
            pdf.setFillColor(26, 26, 26)
            pdf.roundedRect(M, y, pageW - M*2, 22, 3, 3, 'F')
            pdfFont(9, 'bold', [255, 255, 255])
            pdf.text(t('admin.arch.preview_total_contract').toUpperCase(), M + 10, y + 14)
            const brand = hex(BRAND)
            pdfFont(13, 'bold', [brand.r, brand.g, brand.b])
            pdf.text('$' + fmt(totalContract), pageW - M - 10, y + 14, { align: 'right' })
            y += 44
        }
        // ── 5.6 Scope Detail (inc / exc)
        if (allInc.length > 0 || filteredExc.length > 0) {
            sectionHead(t('admin.arch.scope_detail'))
            const colW = (pageW - M*2 - 24) / 2
            let ly = y, ry = y
            const gap = 24
            if (allInc.length > 0) {
                pdfFont(9, 'bold', [22, 163, 74])
                pdf.text('✓ ' + t('admin.arch.included_label'), M, ly)
                ly += 14
                for (const b of allInc) {
                    pdfFont(10, 'normal')
                    const lines = pdf.splitTextToSize(b, colW - 18)
                    const h = 12 * lines.length
                    ensure(h + 4)
                    pdfFont(10, 'normal', [22, 163, 74])
                    pdf.text('✔', M, ly + 8)
                    pdfFont(10, 'normal', [51, 51, 51])
                    for (let li = 0; li < lines.length; li++) {
                        pdf.text(lines[li], M + 14, ly + 8 + li * 12)
                    }
                    ly += h + 4
                }
            }
            if (filteredExc.length > 0) {
                const cx = M + colW + gap
                pdfFont(9, 'bold', [220, 38, 38])
                pdf.text(t('admin.arch.not_included_label'), cx, ry)
                ry += 14
                for (const b of filteredExc) {
                    pdfFont(10, 'normal')
                    const lines = pdf.splitTextToSize(b, colW - 18)
                    const h = 12 * lines.length
                    ensure(h + 4)
                    pdfFont(10, 'normal', [220, 38, 38])
                    pdf.text('•', cx, ry + 8)
                    pdfFont(10, 'normal', [102, 102, 102])
                    for (let li = 0; li < lines.length; li++) {
                        pdf.text(lines[li], cx + 14, ry + 8 + li * 12)
                    }
                    ry += h + 4
                }
            }
            y = Math.max(ly, ry) + 14
        }

        // ── 5.7 Important Notes
        const activeTermGroups = getActiveTermsGrouped(estimate)
        if (activeTermGroups.length > 0) {
            y += 6
            sectionHead(t('admin.arch.terms_section_title'))
            const brand = hex(BRAND)
            for (const group of activeTermGroups) {
                if (group.title) {
                    pdfFont(9, 'bold', [102, 102, 102])
                    pdf.text(group.title.toUpperCase(), M, y + 8)
                    y += 14
                }
                for (const term of group.items) {
                    pdfFont(10, 'normal')
                    const cleanText = cleanTermText(term.text)
                    const termW = pageW - M*2 - 18
                    pdf.setLineHeightFactor(1.3)
                    const termLines = wrapToWidth(cleanText, termW)
                    const termLH = pdf.getLineHeight()
                    const h = termLH * termLines.length
                    ensure(h + 6)
                    pdfFont(10, 'bold', [brand.r, brand.g, brand.b])
                    pdf.text('•', M, y + 8)
                    pdfFont(10, 'normal', [51, 51, 51])
                    pdf.setCharSpace(0)
                    let ty = y + 8 - termLH
                    for (const ln of termLines) {
                        ty += termLH
                        if (!ln.last && !ln.broken) drawJustifiedLine(ln.text, M + 14, ty, termW - WRAP_PAD)
                        else pdf.text(ln.text, M + 14, ty)
                    }
                    pdf.setLineHeightFactor(1.15)
                    y += h + 6
                }
                y += 4
            }
        }

        // ── 5.8 Payment Schedule
        if (totalContract > 0 && paySchedule.length > 0) {
            const sub = !schedValid ? `⚠ ${t('admin.arch.schedule_incomplete', { pct: schedTotal })}` : null
            sectionHead(t('admin.arch.payment_schedule'), sub)
            ensure(22)
            pdf.setFillColor(249, 250, 251)
            pdf.rect(M, y, pageW - M*2, 20, 'F')
            pdf.setDrawColor(229, 229, 229); pdf.setLineWidth(0.5)
            pdf.line(M, y + 20, pageW - M, y + 20)
            pdfFont(8, 'bold', [136, 136, 136])
            pdf.text(t('admin.arch.milestone').toUpperCase(), M + 6, y + 13)
            // Pct column hidden per request — still used internally for amount calculation
            // pdf.text(t('admin.arch.pct'), pageW - M - 120 - 6, y + 13, { align: 'right' })
            pdf.text(t('admin.arch.amount_due').toUpperCase(), pageW - M - 6, y + 13, { align: 'right' })
            y += 22
            for (const row of paySchedule) {
                ensure(18)
                const amount = totalContract * ((Number(row.pct) || 0) / 100)
                pdfFont(10, 'normal', [51, 51, 51])
                pdf.text(row.label || (t('admin.arch.payment_fallback') + ' ' + (paySchedule.indexOf(row) + 1)), M + 6, y + 11)
                // Pct cell hidden per request
                // pdfFont(10, 'bold', [136, 136, 136])
                // pdf.text((row.pct || '0') + '%', pageW - M - 120 - 6, y + 11, { align: 'right' })
                pdfFont(10, 'bold')
                pdf.text('$' + fmt(amount), pageW - M - 6, y + 11, { align: 'right' })
                pdf.setDrawColor(240, 240, 240); pdf.setLineWidth(0.5)
                pdf.line(M, y + 16, pageW - M, y + 16)
                y += 18
            }
            y += 14

            // ── What You Should Know (dynamic term)
            const term = getWhatYouShouldKnowTerm(estimate)
            const excText = getWhatYouShouldKnowText(estimate)
            if (term && excText) {
                ensure(14)
                pdfFont(10, 'bold', [26, 26, 26])
                pdf.text(term.section_title || '', M, y + 10)
                y += 14
                pdfFont(10, 'normal', [51, 51, 51])
                pdf.setLineHeightFactor(1.3)
                const paragraphs = excText.split(/\n\n/)
                for (const para of paragraphs) {
                    const pLines = wrapToWidth(para, pageW - M * 2)
                    const pLH = pdf.getLineHeight()
                    ensure(pLH + pLines.length * pLH)
                    let py = y
                    for (const ln of pLines) {
                        py += pLH
                        if (!ln.last && !ln.broken) drawJustifiedLine(ln.text, M, py, pageW - M * 2 - WRAP_PAD)
                        else pdf.text(ln.text, M, py)
                    }
                    y += pLH + pLines.length * pLH + 4
                }
                pdf.setLineHeightFactor(1.15)
                y += 6
            }
        }

        // ── 5.8a Additional Comments (PDF only)
        if (estimate.additionalComments && estimate.additionalComments.trim()) {
            sectionHead(t('admin.arch.preview_additional_comments'))
            const acTextW = pageW - M*2 - 32
            const acText = estimate.additionalComments.trim()
                .replace(/[\u00AD\u200B\u200C\u200D\u200E\u200F\u2028\u2029\uFEFF\u2060]/g, '')
                .replace(/\u00A0/g, ' ')
            pdfFont(10, 'normal', [51, 51, 51])
            pdf.setLineHeightFactor(1.3)
            const acLines = wrapToWidth(acText, acTextW)
            const acLH = pdf.getLineHeight()
            const acBoxH = 14 + acLines.length * acLH + 4
            ensure(acBoxH + 4)
            pdf.setFillColor(250, 250, 250)
            pdf.setDrawColor(229, 229, 229)
            pdf.roundedRect(M, y, pageW - M*2, acBoxH, 3, 3, 'FD')
            pdfFont(10, 'normal', [51, 51, 51])
            pdf.setCharSpace(0)
            let ay = y + 14 - acLH
            for (const ln of acLines) {
                ay += acLH
                if (!ln.last && !ln.broken) drawJustifiedLine(ln.text, M + 12, ay, acTextW - WRAP_PAD)
                else pdf.text(ln.text, M + 12, ay)
            }
            pdf.setLineHeightFactor(1.15)
            y += acBoxH + 14
        }

        // ── 5.9b Preliminary User Info
        if (isPrelimPDF) {
            const preparedByName = preparedBy?.name || ''
            const preparedByEmail = preparedBy?.email || ''
            const preparedByPhone = officeClosing.phone
            if (preparedByName) {
                ensure(60)
                pdfFont(10, 'bold')
                pdf.text(preparedByName, pageW / 2, y, { align: 'center' })
                y += 14
                pdfFont(9, 'italic', [136, 136, 136])
                pdf.text(t('admin.arch.sales_representative'), pageW / 2, y, { align: 'center' })
                y += 14
                if (preparedByEmail) {
                    pdfFont(9, 'normal', [136, 136, 136])
                    pdf.text(preparedByEmail, pageW / 2, y, { align: 'center' })
                    y += 14
                }
                if (preparedByPhone) {
                    pdfFont(9, 'normal', [136, 136, 136])
                    pdf.text(preparedByPhone, pageW / 2, y, { align: 'center' })
                    y += 14
                }
                y += 10
            }
        }

        // ── 5.10 Closing & Signatures
        if (!isPrelimPDF) {
            ensure(60)
            const closing = t('admin.arch.new_closing_text')
            pdfFont(10, 'normal', [51, 51, 51])
            pdf.setLineHeightFactor(1.3)
            const closingLines = wrapToWidth(closing, pageW - M*2)
            const closingLH = pdf.getLineHeight()
            ensure(closingLH + closingLines.length * closingLH)
            pdf.setCharSpace(0)
            let cy = y
            for (const ln of closingLines) {
                cy += closingLH
                if (!ln.last && !ln.broken) drawJustifiedLine(ln.text, M, cy, pageW - M*2 - WRAP_PAD)
                else pdf.text(ln.text, M, cy)
            }
            pdf.setLineHeightFactor(1.15)
            y += closingLH + closingLines.length * closingLH + 10
            pdfFont(10, 'bold')
            pdf.text(t('admin.arch.sincerely'), M, y)
            y += 24
            const preparedByName = preparedBy?.name || ''
            const preparedByEmail = preparedBy?.email || ''
            if (preparedByName) {
                pdfFont(10, 'bold')
                pdf.text(preparedByName, pageW / 2, y, { align: 'center' })
                y += 14
                pdfFont(9, 'italic', [136, 136, 136])
                pdf.text(t('admin.arch.sales_representative'), pageW / 2, y, { align: 'center' })
                y += 14
                if (preparedByEmail) {
                    pdfFont(9, 'normal', [136, 136, 136])
                    pdf.text(preparedByEmail, pageW / 2, y, { align: 'center' })
                    y += 14
                }
                const preparedByPhone = officeClosing.phone
                if (preparedByPhone) {
                    pdfFont(9, 'normal', [136, 136, 136])
                    pdf.text(preparedByPhone, pageW / 2, y, { align: 'center' })
                    y += 14
                }
                y += 10
            }
            if (activeMethods.length > 0) {
                for (const method of activeMethods) {
                    // Pre-compute all line arrays so we can calculate exact block height before ensure()
                    const pmLines = []
                    const accountName = method.account_name || resolveArchOfficeInfo(estimate).companyName
                    if (accountName) pmLines.push(`${t('admin.arch.pm_name')} ${accountName}`)
                    if (method.bank_name) pmLines.push(`${t('admin.arch.pm_bank')} ${method.bank_name}`)
                    if (method.account_number) pmLines.push(`${t('admin.arch.pm_account')} ${method.account_number}`)
                    if (method.routing_number) pmLines.push(`${t('admin.arch.pm_routing')} ${method.routing_number}`)
                    if (method.email) pmLines.push(`${t('admin.arch.pm_email')} ${method.email} ${t('admin.arch.pm_zelle')}`)
                    const cardText = method.card_note || t('admin.arch.pm_card_note')
                    const cardLines = pdf.splitTextToSize(cardText, pageW - M*2)
                    const checkText = method.check_note || t('admin.arch.pm_check_note')
                    const checkLines = pdf.splitTextToSize(checkText, pageW - M*2)
                    const methodH = 10 + 38 + pmLines.length * 15 + 18 + cardLines.length * 11 + 14 + checkLines.length * 11 + 18
                    ensure(methodH)
                    y += 10
                    pdfFont(10, 'bold')
                    pdf.text(t('admin.arch.remit_to'), M, y + 12)
                    y += 38
                    pdfFont(10, 'normal', [85, 85, 85])
                    for (const line of pmLines) {
                        pdf.text(line, M, y)
                        y += 15
                    }
                    y += 18
                    pdfFont(10, 'italic', [85, 85, 85])
                    for (const line of cardLines) {
                        pdf.text(line, M, y)
                        y += 11
                    }
                    y += 14
                    for (const line of checkLines) {
                        pdf.text(line, M, y)
                        y += 11
                    }
                    y += 18
                }
            } else {
                ensure(40)
                y += 10
                pdfFont(10, 'bold')
                pdf.text(t('admin.arch.remit_to'), M, y + 12)
                y += 38
                pdfFont(10, 'normal', [85, 85, 85])
                const accountName = resolveArchOfficeInfo(estimate).companyName
                if (accountName) {
                    pdf.text(`${t('admin.arch.pm_name')} ${accountName}`, M, y)
                    y += 15
                }
                y += 4
                y += 10
            }

            ensure(110)
            const brand = hex(BRAND)
            pdf.setDrawColor(brand.r, brand.g, brand.b); pdf.setLineWidth(2)
            pdf.line(M, y, pageW - M, y)
            y += 14
            // CLIENT label + name
            pdfFont(8, 'bold', [136, 136, 136])
            pdf.text(t('admin.arch.sig_client').toUpperCase(), M, y)
            let sigFy = y + 18
            if (estimate.client) {
                pdfFont(11, 'normal', [26, 26, 26])
                pdf.text(estimate.client, M, y + 14)
                sigFy = y + 30
            }
            // 3-column signature area
            const sigZoneGap = 12
            const sigZoneH   = 34
            const sigZoneW   = 210
            const dateZoneW  = 128
            const nameZoneW  = (pageW - 2*M) - sigZoneW - dateZoneW - 2*sigZoneGap
            const sigZoneX   = M
            const dateZoneX  = M + sigZoneW + sigZoneGap
            const nameZoneX  = M + sigZoneW + dateZoneW + 2*sigZoneGap
            function drawSigZone(label, zx, zy, zw, zh) {
                pdfFont(7, 'bold', [170, 170, 170])
                pdf.text(label, zx, zy)
                pdf.setLineDashPattern([2, 2], 0)
                pdf.setDrawColor(187, 187, 187); pdf.setLineWidth(0.5)
                pdf.roundedRect(zx, zy + 4, zw, zh, 2, 2)
                pdf.setLineDashPattern([], 0)
            }
            // Signature — visual placeholder only (GHL handles actual e-signature)
            drawSigZone('SIGNATURE', sigZoneX, sigFy, sigZoneW, sigZoneH)
            // Date — AcroForm text field
            drawSigZone('DATE', dateZoneX, sigFy, dateZoneW, sigZoneH)
            addAcroTextField({ name: 'sig_date', x: dateZoneX, yTop: sigFy + 4, w: dateZoneW, h: sigZoneH, fontSize: 10, value: 'MM/DD/YYYY' })
            // Full Name — AcroForm text field
            drawSigZone('FULL NAME', nameZoneX, sigFy, nameZoneW, sigZoneH)
            addAcroTextField({ name: 'sig_fullname', x: nameZoneX, yTop: sigFy + 4, w: nameZoneW, h: sigZoneH, fontSize: 21, maxLength: 21 })
            y += 110
        }

        // ── 5.10 Company Footer
        ensure(48)
        pdf.setDrawColor(229, 229, 229); pdf.setLineWidth(0.5)
        pdf.line(M, y, pageW - M, y)
        y += 10
        pdfFont(9, 'bold')
        pdf.text(officeClosing.companyName.toUpperCase(), M, y)
        pdfFont(9, 'normal', [136, 136, 136])
        pdf.text(officeClosing.address, M, y + 12)
        pdf.text(COMPANY.email, pageW - M, y, { align: 'right' })
        pdf.text(COMPANY.website, pageW - M, y + 12, { align: 'right' })

        // ── Page numbers
        const totalPages = pdf.internal.getNumberOfPages()
        for (let i = 1; i <= totalPages; i++) {
            pdf.setPage(i)
            pdfFont(8, 'normal', [255, 255, 255])
            pdf.text(t('admin.arch.page_number', { current: i, total: totalPages }), pageW - M, pageH - ftrH + 32, { align: 'right' })
        }

        // ── Initials zones (every page except the last)
        const initialsW = 62, initialsH = 28
        const initialsX = pageW - M - initialsW
        const initialsY = pageH - ftrH - 46
        for (let i = 1; i < totalPages; i++) {
            pdf.setPage(i)
            pdf.setLineDashPattern([2, 2], 0)
            pdf.setDrawColor(180, 180, 180); pdf.setLineWidth(0.75)
            pdf.roundedRect(initialsX, initialsY, initialsW, initialsH, 3, 3)
            pdf.setLineDashPattern([], 0)
            pdfFont(7, 'normal', [170, 170, 170])
            pdf.text('Initials', initialsX + initialsW / 2, initialsY - 4, { align: 'center' })
            addAcroTextField({ name: `initials_p${i}`, x: initialsX, yTop: initialsY, w: initialsW, h: initialsH, fontSize: 13, align: 1, maxLength: 2 })
        }

        // ── Save
        const prefix = isPrelim ? 'arch-preliminary' : 'arch-proposal'
        const fileName = prefix + '-' + (estimate.number || 'draft').replace(/[^a-zA-Z0-9-]/g, '_') + '.pdf'
        pdf.save(fileName)
    }

    const handleExport = async () => {
        const MAIN_PCT_FIELDS = ['archPct', 'structPct', 'mechPct', 'elecPct', 'plumbPct', 'adminPct']
        const CIVIL_PCT_FIELDS = ['civilPct', 'civilAdminPct']
        const PCT_EPSILON = 0.01
        const sumPcts = (src, fields) => fields.reduce((s, k) => s + (Number(src?.[k]) || 0), 0)

        const useCivilSplit = !!estimate?.useCivilSplit
        const isAddAlt = ADD_ALT_PROJECT_TYPES.has(estimate?.projectType)
        const isCivilProject = estimate?.projectType === 'civil'
        const scopeIncludesCivil = (estimate?.disciplineScope || 'full') === 'full' || (estimate?.disciplineScope || '').includes('civil')

        let poolsOff = []
        if (useCivilSplit) {
            // Two independent pools — each must sum to 100% on its own.
            // Pure civil projects are exempt from the building pool check (matches UI).
            const mainSum = sumPcts(estimate, MAIN_PCT_FIELDS)
            if (!isCivilProject && Math.abs(mainSum - 100) >= PCT_EPSILON) {
                poolsOff.push(`${t('admin.arch.pct_pool_main')}: ${mainSum}%`)
            }
            // Civil pool only matters when the scope actually includes civil disciplines.
            if (scopeIncludesCivil) {
                const civilSum = sumPcts(estimate, CIVIL_PCT_FIELDS)
                if (Math.abs(civilSum - 100) >= PCT_EPSILON) {
                    poolsOff.push(`${t('admin.arch.pct_pool_civil')}: ${civilSum}%`)
                }
            }
        } else {
            // Single pool — civilPct rides along unless this is Add/Alt/Rem.
            const poolFields = isAddAlt ? MAIN_PCT_FIELDS : [...MAIN_PCT_FIELDS, 'civilPct']
            const totalSum = sumPcts(estimate, poolFields)
            if (Math.abs(totalSum - 100) >= PCT_EPSILON) {
                poolsOff.push(`${t('admin.arch.pct_pool_main')}: ${totalSum}%`)
            }
        }

        if (poolsOff.length > 0) {
            alert(t('admin.arch.pct_export_blocked', { pools: poolsOff.join(' | ') }))
            return
        }

        setExporting(true)
        try {
            const englishT = (key, options) => i18n.t(key, { lng: 'en', ...options })
            await buildPDF(englishT, 'en-US', preparedBy)
        }
        catch (e) {
            console.error('[ArchEstimatePreview] PDF error:', e)
            const msg = e?.message || (typeof e === 'string' ? e : t('admin.arch.unknown_error'))
            alert(t('admin.arch.export_failed', { message: msg }))
        }
        finally { setExporting(false) }
    }

    return (
        <div>
            <div className="sticky top-0 z-10 px-2 py-2 -mx-2">
                <div className="flex justify-between items-center">
                    {isPrelim
                        ? <span className="px-3 py-1 text-xs font-bold text-amber-800 bg-amber-100 rounded-full border border-amber-300">⚡ {t('admin.arch.preliminary_badge')}</span>
                        : <span />
                    }
                    <div className="flex gap-3 items-center">
                        <div className="flex items-center bg-gray-100 rounded-lg p-0.5 shadow-md">
                            <button
                                onClick={() => setPdfLang('en')}
                                className={`px-2.5 py-1 text-xs font-semibold rounded-md transition-all ${pdfLang === 'en' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                            >EN</button>
                            <button
                                onClick={() => setPdfLang('es')}
                                className={`px-2.5 py-1 text-xs font-semibold rounded-md transition-all ${pdfLang === 'es' ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                            >ES</button>
                        </div>
                        <button
                            onClick={handleExport}
                            disabled={exporting}
                            className="flex gap-2 items-center px-4 py-2 text-sm font-semibold text-white rounded-xl shadow-lg transition-all bg-primary hover:bg-primary/90 disabled:opacity-60"
                        >
                            {exporting ? <FaSpinner size={13} className="animate-spin" /> : <FaFilePdf size={13} />}
                            {exporting ? t('admin.arch.exporting_pdf') : t('admin.arch.export_pdf')}
                        </button>
                    </div>
                </div>
            </div>
            <div className="overflow-x-auto rounded-2xl shadow-md">
                <div style={{ width: 760, margin: '0 auto', background: '#fff' }}>
                    <ArchProposalDocument estimate={estimate} mode={mode} difficultyLevels={difficultyLevels} paymentInfo={paymentInfo} t={pdfT} locale={pdfLang === 'es' ? 'es-ES' : 'en-US'} session={session} addonDefs={addonDefs} />
                </div>
            </div>
        </div>
    )
}
